import { useMemo } from 'react';

import { AppTracker, EventSchema } from 'types';
import { downloadFile, generateCalendarEvent, getCalendarFileName } from 'utils';
import { channelByNameSelector, membershipChannelsSelector, profileChannelNameSelector } from 'store/selectors';
import { LoggerService, tracker } from 'services';
import { useActiveEventsQuery, usePastEventsQuery, useStore, useToast } from 'hooks';
import { EventCard, LoadMore } from 'components';
import { Spinner } from 'components/Icon';

import styles from './ChannelDetails.module.scss';

export type ChannelEventsProps = {
  channelName: string;
  activeTab: string;
  onWatch?(channelName: string, eventCode: string): void;
};

const ChannelEvents = ({ activeTab, channelName, onWatch }: ChannelEventsProps): JSX.Element => {
  const toast = useToast();
  const channel = useStore(channelByNameSelector(channelName));
  const { ownChannelName, membershipChannels } = useStore(state => ({
    ownChannelName: profileChannelNameSelector(state),
    membershipChannels: membershipChannelsSelector(state)
  }));

  const activeEventsQuery = useActiveEventsQuery();
  const pastEventsQuery = usePastEventsQuery(channel?.id);

  const { ongoing: ongoingEvents, scheduled: scheduledEvents } = useMemo(() => {
    const data = activeEventsQuery.data?.data;
    const ongoing = channelName ? data?.ongoing.filter(event => event.channelName === channelName) : data?.ongoing;
    const scheduled = channelName
      ? data?.scheduled.filter(event => event.channelName === channelName)
      : data?.scheduled;

    return { ongoing, scheduled };
  }, [activeEventsQuery.data?.data, channelName]);

  const sortedOngoingEvents = useMemo(
    () =>
      ongoingEvents?.sort((prev, curr) => new Date(curr.startDate).getTime() - new Date(prev.startDate).getTime()) ||
      [],
    [ongoingEvents]
  );

  const sortedUpcomingEvents = useMemo(
    () =>
      scheduledEvents?.sort((prev, curr) => new Date(prev.startDate).getTime() - new Date(curr.startDate).getTime()) ||
      [],
    [scheduledEvents]
  );

  const handleOnAddToCalendar = async (event: EventSchema) => {
    try {
      tracker.addToCalendar(
        event.code,
        channelName ? AppTracker.AddToCalendarSource.CHANNEL_PAGE : AppTracker.AddToCalendarSource.HOME_PAGE
      );
      const file = await generateCalendarEvent(event);

      downloadFile(file, getCalendarFileName(event.code));
      tracker.addToCalendarSuccess(
        event.code,
        channelName ? AppTracker.AddToCalendarSource.CHANNEL_PAGE : AppTracker.AddToCalendarSource.HOME_PAGE
      );
    } catch (error) {
      LoggerService.log('ADD_TO_CALENDAR', error);

      if (error instanceof Error) {
        tracker.addToCalendarFail(
          event.code,
          channelName ? AppTracker.AddToCalendarSource.CHANNEL_PAGE : AppTracker.AddToCalendarSource.HOME_PAGE,
          error
        );
        toast.error(error.message, { id: `ADD_TO_CALENDAR:${event.code}` });
      } else if (typeof error === 'string') {
        toast.error(error, { id: `ADD_TO_CALENDAR:${event.code}` });
      }
    }
  };

  const renderOngoingEvents = () => {
    if (activeEventsQuery.isLoading && !activeEventsQuery.isFetched) {
      return (
        <div className="text-center py-42">
          <Spinner />
        </div>
      );
    }

    if (sortedOngoingEvents.length === 0) {
      return <div className={styles.empty}>No ongoing events found</div>;
    }

    return (
      <>
        <ul className={styles.list}>
          {sortedOngoingEvents.map(event => {
            const hasAccess = event.channelName === ownChannelName || !!membershipChannels[event.channelName];

            return (
              <li key={event.code}>
                <EventCard.Ongoing
                  eventCode={event.code}
                  eventName={event.name}
                  startDate={event.startDate}
                  channelName={event.channelName}
                  isMembersOnly={event.memberOnly}
                  hasAccess={hasAccess}
                  poster={event.poster}
                  onClick={() => onWatch?.(event.channelName, event.code)}
                />
              </li>
            );
          })}
        </ul>
        <LoadMore autoFetch query={pastEventsQuery} />
      </>
    );
  };

  const renderUpcomingEvents = () => {
    if (activeEventsQuery.isLoading && !activeEventsQuery.isFetched) {
      return (
        <div className="text-center py-42">
          <Spinner />
        </div>
      );
    }

    if (sortedUpcomingEvents.length === 0) {
      return <div className={styles.empty}>No scheduled events found</div>;
    }

    return (
      <ul className={styles.list}>
        {sortedUpcomingEvents.map(event => {
          const hasAccess = event.channelName === ownChannelName || !!membershipChannels[event.channelName];

          return (
            <li key={event.code}>
              <EventCard.Upcoming
                eventCode={event.code}
                eventName={event.name}
                startDate={event.startDate}
                channelName={event.channelName}
                isMembersOnly={event.memberOnly}
                hasAccess={hasAccess}
                poster={event.poster}
                onClick={() => onWatch?.(event.channelName, event.code)}
                onAddToCalendar={() => handleOnAddToCalendar(event)}
              />
            </li>
          );
        })}
      </ul>
    );
  };

  const renderPastEvents = () => {
    if (pastEventsQuery.isLoading && !pastEventsQuery.isFetched) {
      return (
        <div className="text-center py-42">
          <Spinner />
        </div>
      );
    }

    if (pastEventsQuery.data?.pages[0].data.totalElements === 0) {
      return <div className={styles.empty}>No recorded events found</div>;
    }

    return pastEventsQuery.data?.pages.map(page => (
      <ul key={page.data.number} className={styles.list}>
        {page.data.content.map(event => {
          const isOwnChannel = event.channelName === ownChannelName;
          const hasAccess = isOwnChannel || !!membershipChannels[event.channelName];
          const isHidden = !!event.hidePastEvent;

          return (
            <li key={event.code}>
              <EventCard.Past
                eventCode={event.code}
                eventName={event.name}
                startDate={event.startDate}
                duration={event.stats.duration}
                recordingWatchCount={event.stats.recordingWatchCount}
                channelName={event.channelName}
                isMembersOnly={event.memberOnly}
                hasAccess={hasAccess}
                poster={event.poster || undefined}
                isHidden={isHidden}
                onClick={() => onWatch?.(event.channelName, event.code)}
              />
            </li>
          );
        })}
      </ul>
    ));
  };

  const render = () => {
    switch (activeTab) {
      case 'upcoming':
        return renderUpcomingEvents();

      case 'past':
        return renderPastEvents();

      case 'ongoing':
      default:
        return renderOngoingEvents();
    }
  };

  return <>{render()}</>;
};

export default ChannelEvents;
